/**
 * Competitive analysis tab (P5.1d).
 *
 * Mounted as the Competitive tab on /ads-tools. Surfaces Auction Insights
 * — which other domains showed up in the same search auctions as the
 * tenant — over time. Primary flow is import of an exported report; the
 * live pull remains as a diagnostic backfill path.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarDays,
  Copy,
  ExternalLink,
  Loader2,
  Swords,
  Trophy,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  listAuctionInsights,
  importCompetitiveAuctionInsights,
  triggerCompetitivePull,
  type AuctionInsightRow,
} from '@/services/googleAdsCompetitiveService';
import { listLiveCampaigns } from '@/services/googleAdsCampaignsService';

interface CompetitiveTabProps {
  tenantId: string;
}

export function CompetitiveTab({ tenantId }: CompetitiveTabProps) {
  const queryClient = useQueryClient();
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
  const [campaignId, setCampaignId] = useState('');
  const [exportFile, setExportFile] = useState<File | null>(null);
  const exportInputRef = useRef<HTMLInputElement | null>(null);
  const exportHelperCommand = 'python backend/scripts/export_google_ads_auction_insights.py';
  const exportHelperUrl = 'https://ads.google.com/aw/overview';
  const liveCampaigns = useQuery({
    queryKey: ['google-ads-live-campaigns', tenantId],
    queryFn: () => listLiveCampaigns({ tenantId, limit: 200 }),
    enabled: Boolean(tenantId),
  });
  const defaultPeriod = useMemo(() => {
    const end = new Date();
    end.setDate(end.getDate() - 1);
    const start = new Date(end);
    start.setDate(start.getDate() - 6);
    return {
      start: toDateInputValue(start),
      end: toDateInputValue(end),
    };
  }, []);
  const [periodStart, setPeriodStart] = useState(defaultPeriod.start);
  const [periodEnd, setPeriodEnd] = useState(defaultPeriod.end);

  const selectableCampaigns = useMemo(
    () =>
      (liveCampaigns.data?.campaigns ?? [])
        .filter((campaign) => campaign.live_in_google_ads && campaign.campaign_id)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [liveCampaigns.data?.campaigns],
  );

  useEffect(() => {
    if (campaignId || selectableCampaigns.length === 0) return;
    const defaultCampaign =
      selectableCampaigns.find((campaign) =>
        /park city/i.test(campaign.name || ''),
      ) ?? selectableCampaigns[0];
    if (defaultCampaign?.campaign_id) {
      setCampaignId(defaultCampaign.campaign_id);
    }
  }, [campaignId, selectableCampaigns]);

  const insights = useQuery({
    queryKey: ['google-ads-auction-insights', tenantId],
    queryFn: () => listAuctionInsights(tenantId, 200),
    refetchInterval: 60_000,
  });

  const importMutation = useMutation({
    mutationFn: () => {
      if (!exportFile) {
        throw new Error('Select a Google Ads export file first.');
      }
      return importCompetitiveAuctionInsights({
        tenantId,
        file: exportFile,
        campaignId: campaignId.trim() || undefined,
      });
    },
    onSuccess: (result) => {
      if (result.skipped_reason) {
        toast.info(
          `No competitor rows were imported from ${result.file_name ?? 'the export'} ` +
            `(${result.skipped_reason})`,
        );
      } else {
        toast.success(
          `Imported ${result.competitor_count} competitor row(s) from ` +
            `${result.file_name ?? 'the export'}`,
        );
      }
      if (result.period_start && result.period_end) {
        setSelectedPeriod(`${result.period_start}__${result.period_end}`);
      }
      queryClient.invalidateQueries({
        queryKey: ['google-ads-auction-insights', tenantId],
      });
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Import failed: ${detail}`);
    },
  });

  const triggerMutation = useMutation({
    mutationFn: () => triggerCompetitivePull({
      tenantId,
      campaignId: campaignId.trim() || undefined,
      periodStart: periodStart || undefined,
      periodEnd: periodEnd || undefined,
    }),
    onSuccess: (result) => {
      if (result.skipped_reason) {
        // CORE-CI-02: a backend `skipped_reason` is a failure to fetch,
        // not a benign "info" about missing data. The old toast said
        // "No competitor data for ..." as info, so the operator saw a
        // grey banner and moved on believing the account was quiet. We
        // now render it as an error and quote the reason plainly so
        // the operator can act on it (reconnect, retry the window,
        // fall back to the export path).
        toast.error(
          `Live pull failed for ${result.period_start} → ${result.period_end}: ` +
            `${result.skipped_reason}`,
        );
      } else {
        toast.success(
          `Pulled ${result.competitor_count} competitor row(s) for ` +
            `${result.period_start} → ${result.period_end}`,
        );
      }
      queryClient.invalidateQueries({
        queryKey: ['google-ads-auction-insights', tenantId],
      });
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Trigger failed: ${detail}`);
    },
  });

  // Group rows by period for the period picker + table
  const grouped = groupByPeriod(insights.data ?? []);
  const periods = Object.keys(grouped).sort().reverse();
  const activePeriod = selectedPeriod ?? periods[0] ?? null;
  const activeRows = activePeriod ? grouped[activePeriod] : [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Swords className="h-4 w-4 text-coral" />
            Import Auction Insights
          </CardTitle>
          <CardDescription>
            Export the Google Ads Auction Insights report, upload it here,
            and the backend saves the competitor rows for the table below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* CORE-CI-03: the two panels below (file import + live
              pull) stack vertically at every breakpoint. They used to
              side-by-side at xl (a two-column responsive grid), which
              meant a wide-screen operator had to compare two dense
              forms across the horizontal axis while reading the table
              below. Stacked, the import panel reads as the primary
              path (import first, live pull as fallback per the
              caption underneath) and the two panels line up with the
              Competitor visibility table's full width. */}
          <div className="flex flex-col gap-4">
            <div className="space-y-4 rounded-lg border border-border/60 bg-muted/20 p-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  Auction Insights export
                </label>
                <p className="text-xs text-muted-foreground">
                  Download the report from Google Ads, then import it here.
                  The backend stores the normalized rows in the database.
                </p>
              </div>

              <input
                ref={exportInputRef}
                id="competitive-export-file"
                type="file"
                accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values"
                onChange={(event) => {
                  setExportFile(event.target.files?.[0] ?? null);
                }}
                className="sr-only"
              />

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => exportInputRef.current?.click()}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Choose export file
                </Button>
                <span className="min-w-0 flex-1 text-sm text-muted-foreground">
                  {exportFile ? exportFile.name : 'No file selected'}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  onClick={() => importMutation.mutate()}
                  disabled={importMutation.isPending || !exportFile}
                  className="bg-coral hover:bg-coral-hover"
                >
                  {importMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-2 h-4 w-4" />
                  )}
                  Import export
                </Button>
                <p className="text-xs text-muted-foreground">
                  Upload the exported file to save competitor rows.
                </p>
              </div>

              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Open export helper
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-96 space-y-3">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Export helper</p>
                    <p className="text-xs text-muted-foreground">
                      Open Google Ads, then run the local helper from the repo
                      root to capture the next Auction Insights download.
                    </p>
                  </div>
                  <div className="rounded border border-border bg-muted/30 p-3">
                    <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      Helper command
                    </p>
                    <code className="block overflow-x-auto rounded bg-background px-2 py-1 text-xs text-foreground">
                      {exportHelperCommand}
                    </code>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(exportHelperUrl, '_blank', 'noopener,noreferrer')}
                    >
                      <ExternalLink className="mr-2 h-3.5 w-3.5" />
                      Open Google Ads
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        if (!navigator.clipboard?.writeText) {
                          toast.error('Clipboard copy is not available in this browser.');
                          return;
                        }
                        await navigator.clipboard.writeText(exportHelperCommand);
                        toast.success('Helper command copied');
                      }}
                    >
                      <Copy className="mr-2 h-3.5 w-3.5" />
                      Copy command
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-4 rounded-lg border border-border/60 bg-muted/20 p-4">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_160px_160px]">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    Campaign
                  </label>
                  <select
                    value={campaignId}
                    onChange={(e) => setCampaignId(e.target.value)}
                    className="h-10 w-full rounded border border-input bg-background px-3 text-sm"
                    disabled={
                      liveCampaigns.isLoading || selectableCampaigns.length === 0
                    }
                  >
                    <option value="">Select a campaign</option>
                    {selectableCampaigns.map((campaign) => (
                      <option
                        key={campaign.campaign_id}
                        value={campaign.campaign_id ?? ''}
                      >
                        {campaign.name}{' '}
                        {campaign.campaign_id ? `(${campaign.campaign_id})` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-muted-foreground">
                    Optional: preserve a campaign ID on imported rows.
                  </p>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    Period start
                  </label>
                  <input
                    type="date"
                    value={periodStart}
                    onChange={(e) => setPeriodStart(e.target.value)}
                    className="h-10 w-full rounded border border-input bg-background px-3 text-sm"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    Period end
                  </label>
                  <input
                    type="date"
                    value={periodEnd}
                    onChange={(e) => setPeriodEnd(e.target.value)}
                    className="h-10 w-full rounded border border-input bg-background px-3 text-sm"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={() => triggerMutation.mutate()}
                  disabled={triggerMutation.isPending || !campaignId}
                  className="bg-coral hover:bg-coral-hover"
                >
                  {triggerMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Extracting… (~10-30s)
                    </>
                  ) : (
                    <>
                      <Swords className="mr-2 h-4 w-4" />
                      Extract report
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            The live pull extracts from Google Ads directly. Importing the
            exported file remains the fallback when live extraction is blocked
            or empty.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-teal" />
            Competitor visibility
          </CardTitle>
          <CardDescription>
            Per-competitor metrics from your Google Ads auctions. Higher
            impression share = competitor wins more head-to-head impressions.
            Higher overlap rate = you frequently appear together.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {insights.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : insights.isError ? (
            // CORE-CI-02: a failed insights read is NOT "no competitor
            // data". It's a load error and reads that way now — with the
            // backend detail visible (not swallowed) and a Retry that
            // re-runs the query. Without this branch, an error dropped
            // through to the "No competitor data yet" block below,
            // pretending the account had no competitors when the fetch
            // never landed.
            <CompetitiveInsightsErrorPanel
              error={insights.error}
              onRetry={() => insights.refetch()}
              isRetrying={insights.isFetching}
            />
          ) : periods.length === 0 ? (
            <div
              className="rounded border border-dashed border-border p-6 text-center text-sm text-muted-foreground"
              data-testid="competitive-empty-state"
            >
              No competitor rows in the store yet for this workspace. Import
              a Google Ads export above to populate this table, or use the
              live pull if you need to test the API path.
            </div>
          ) : (
            <div className="space-y-4">
              {periods.length > 1 && (
                <div className="flex flex-wrap gap-2">
                  {periods.map((p) => (
                    <Button
                      key={p}
                      size="sm"
                      variant={p === activePeriod ? 'default' : 'outline'}
                      className={
                        p === activePeriod
                          ? 'bg-teal hover:bg-teal-hover'
                          : ''
                      }
                      onClick={() => setSelectedPeriod(p)}
                    >
                      {formatPeriodLabel(p)}
                    </Button>
                  ))}
                </div>
              )}

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Competitor</TableHead>
                    <TableHead className="text-right">Impr. share</TableHead>
                    <TableHead className="text-right">Overlap rate</TableHead>
                    <TableHead className="text-right">Top of page</TableHead>
                    <TableHead className="text-right">Abs top</TableHead>
                    <TableHead className="text-right">Position above</TableHead>
                    <TableHead className="text-right">Outranking</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeRows
                    .slice()
                    .sort(
                      (a, b) =>
                        (b.impression_share ?? 0) - (a.impression_share ?? 0),
                    )
                    .map((row, idx) => (
                      <CompetitorRow
                        key={row.id}
                        row={row}
                        rank={idx + 1}
                      />
                    ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// CORE-CI-02: named panel (bus-factor policy §8) that reads as a load
// error, not a state of the world. Extracts the backend detail out of
// axios-shaped or plain-Error rejections and shows a Retry that
// re-runs the underlying useQuery. If neither retry nor Chans's rule
// finds anything, the operator sees the exact string the backend
// returned instead of a friendly lie.
function CompetitiveInsightsErrorPanel({
  error,
  onRetry,
  isRetrying,
}: {
  error: unknown;
  onRetry: () => void;
  isRetrying: boolean;
}) {
  const detail = extractCompetitiveErrorDetail(error);
  return (
    <div
      className="rounded border border-destructive/40 bg-destructive/5 p-4 text-sm"
      role="alert"
      data-testid="competitive-error-panel"
    >
      <div className="mb-1 flex items-center gap-2 font-medium text-destructive">
        <Swords className="h-4 w-4" />
        Could not load competitor visibility
      </div>
      <p className="text-muted-foreground">
        The Competitive Insights read failed. This is not "no competitor
        data" — the fetch itself did not land. Retry the load below, or
        open the network tab if this keeps happening.
      </p>
      <p className="mt-2 break-words font-mono text-xs text-destructive">
        {detail}
      </p>
      <div className="mt-3">
        <Button
          size="sm"
          variant="outline"
          onClick={onRetry}
          disabled={isRetrying}
        >
          {isRetrying ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Retrying…
            </>
          ) : (
            'Retry load'
          )}
        </Button>
      </div>
    </div>
  );
}

function extractCompetitiveErrorDetail(err: unknown): string {
  // Axios-shaped: response.data.detail carries the FastAPI error string.
  // Plain Error: use message. Fall back to String(err) so the operator
  // never reads "undefined" or the empty string.
  const axiosDetail = (err as {
    response?: { data?: { detail?: string } };
  })?.response?.data?.detail;
  if (typeof axiosDetail === 'string' && axiosDetail.trim()) {
    return axiosDetail;
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  // String(err) for a bare object is "[object Object]", which is worse
  // than useless — it reads as noise and gives the operator nothing to
  // act on. Treat that (and empty strings) as "no detail".
  const stringified = String(err);
  if (stringified && stringified !== '[object Object]') {
    return stringified;
  }
  return 'Unknown error (no detail returned).';
}

function CompetitorRow({
  row,
  rank,
}: {
  row: AuctionInsightRow;
  rank: number;
}) {
  return (
    <TableRow>
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          {rank <= 3 && <Trophy className="h-3 w-3 text-coral" />}
          {row.competitor_domain}
          {rank === 1 && (
            <Badge variant="secondary" className="text-xs">
              Top
            </Badge>
          )}
        </div>
      </TableCell>
      <Pct value={row.impression_share} />
      <Pct value={row.overlap_rate} />
      <Pct value={row.top_of_page_rate} />
      <Pct value={row.abs_top_of_page_rate} />
      <Pct value={row.position_above_rate} />
      <Pct value={row.outranking_share} />
    </TableRow>
  );
}

function Pct({ value }: { value: number | null }) {
  return (
    <TableCell className="text-right tabular-nums">
      {value === null || value === undefined
        ? '—'
        : `${(value * 100).toFixed(1)}%`}
    </TableCell>
  );
}

function groupByPeriod(
  rows: AuctionInsightRow[],
): Record<string, AuctionInsightRow[]> {
  const out: Record<string, AuctionInsightRow[]> = {};
  for (const r of rows) {
    const key = `${r.period_start}__${r.period_end}`;
    if (!out[key]) out[key] = [];
    out[key].push(r);
  }
  return out;
}

function formatPeriodLabel(periodKey: string): string {
  const [start, end] = periodKey.split('__');
  try {
    const s = new Date(start);
    const e = new Date(end);
    const fmt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    return `${s.toLocaleDateString('en-US', fmt)} – ${e.toLocaleDateString('en-US', fmt)}`;
  } catch {
    return `${start} → ${end}`;
  }
}

function toDateInputValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}
